import React, { useState, useEffect } from "react";
import styled from "styled-components";
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { useNavigate } from 'react-router-dom';
import adminBg from '../Assets/bg/adminbg.jpg';
import ConvergenceAdd from "./convergenceAdd";
import ConvergenceImportPreview from "./convergenceImportPreview";
import useConvergence from "../Hooks/ConvergenceHooks/useConvergence";
import useAddInsertConvergence from "../Hooks/ConvergenceHooks/useAddInsertConvergence";

// Helper constants
const months = [
  "Jan-31", "Feb-28", "Mar-31", "Apr-30", "May-31", "Jun-30",
  "Jul-30", "Aug-30", "Sept-30", "Oct-30", "Nov-30", "Dec-30"
];

const initialPrograms = [
  { program: "EPAHP", indicators: ["No. of Marketing Agreements", "Gross Sales (Php)"] },
  { program: "PCB Project - SLP", indicators: ["No. of Projects", "No. of Beneficiaries (IA Members)", "Gross Sales (Php)"] },
  { program: "Cash for Work", indicators: ["No. of Beneficiaries (IA Members)", "Cash Counterpart (Php)"] },
  { program: "Walang Gutom Program", indicators: ["No. of IA", "No. of Beneficiaries (IA Members)"] },
  { program: "Family Food Packs", indicators: ["No. of IA", "No. of Beneficiaries (IA Members)"] },
  { program: "Food for Work", indicators: ["No. of IA", "No. of Beneficiaries (IA Members)"] },
  { program: "Gulayan sa Pamayanan", indicators: ["No. of Beneficiaries (IA Members)"] },
  { program: "LAWA at BINHI", indicators: ["No. of IA", "IA Members"] },
  { program: "Financial Assistance", indicators: ["No. of Beneficiaries (IA Members)", "Area(ha)", "Cash Counterpart (Php)"] },
  { program: "Trainings", indicators: ["No. of Trainings", "No. of IA", "No. of Batches", "No. of Farmers trained (IA Members)"] }
];

// Helper functions
function initialState() {
  return initialPrograms.map(p => ({
    type: p.type || "defaultType", // or some default value
    program: p.program,
    data: p.indicators.map(ind => ({
      id: null,
      indicator: ind,
      status2024: "",
      target2025: "",
      months: months.map(() => ""),
      total: "",
      statusToDate: "",
      remarks: ""
    }))
  }));
}


function transformFromApi(rows) {
  const grouped = {};
  rows.forEach(r => {
    const key = r.program;
    if (!grouped[key]) grouped[key] = { type: r.type, program: r.program, data: [] };
    grouped[key].data.push(r);
  });
  return Object.values(grouped).map(g => ({
    type: g.type,
    program: g.program,
    data: g.data.map(r => ({
      id: r.id,
      indicator: r.indicator,
      status2024: r.status_2024,
      target2025: r.target_2025,
      months: [
        r.accom_jan, r.accom_feb, r.accom_mar, r.accom_apr,
        r.accom_may, r.accom_jun, r.accom_jul, r.accom_aug,
        r.accom_sep, r.accom_oct, r.accom_nov, r.accom_dec
      ],
      total: r.total,
      statusToDate: r.status_date,
      remarks: r.remarks
    }))
  }));
}

function flattenForApi(programData) {
  return programData.flatMap(p => p.data.map(entry => ({
    id: entry.id,
    type: p.type,
    program: p.program,
    indicator: entry.indicator,
    status_2024: entry.status2024,
    target_2025: entry.target2025,
    accom_jan: entry.months[0],
    accom_feb: entry.months[1],
    accom_mar: entry.months[2],
    accom_apr: entry.months[3],
    accom_may: entry.months[4],
    accom_jun: entry.months[5],
    accom_jul: entry.months[6],
    accom_aug: entry.months[7],
    accom_sep: entry.months[8],
    accom_oct: entry.months[9],
    accom_nov: entry.months[10],
    accom_dec: entry.months[11],
    total: entry.total,
    status_date: entry.statusToDate,
    remarks: entry.remarks
  })));
}

function mergeResponse(oldData, savedRows) {
  const map = Object.fromEntries(savedRows.map(r => [r.tempId ?? r.id, r]));
  return oldData.map(prog => ({
    ...prog,
    data: prog.data.map(entry => {
      const key = entry.id ?? entry.tempId;
      const match = map[key];
      return match ? { ...entry, id: match.id } : entry;
    })
  }));
}

function groupByType(data) {
  return data.reduce((acc, item) => {
    if (!acc[item.type]) acc[item.type] = [];
    acc[item.type].push(item);
    return acc;
  }, {});
}


// Main component
function ConvergenceMonitoringForm({ forceViewOnly = false, forceEditable = false, onBack, onSelect } = {}) {
  const navigate = useNavigate();
  const fileInputRef = React.useRef();
  const { data, loading, error } = useConvergence();
  const [programData, setProgramData] = useState([]);
  const { upsertRows, saving } = useAddInsertConvergence();
  const [selectedType, setSelectedType] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const DropdownContainer = styled.div.attrs(() => ({ className: 'dropdown' }))`
  position: relative;
`;
  const [previewData, setPreviewData] = useState([]);
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  const userRole = typeof window !== "undefined" ? localStorage.getItem("role") : "";
  const isViewOnly = !forceEditable && (forceViewOnly || (userRole || "").toLowerCase() === "noso");
  const allowEditing = !isViewOnly;

  const filteredData = selectedType
    ? programData.filter((p) => p.type === selectedType)
    : programData;
  const groupedByType = groupByType(filteredData);

  const [showAddForm, setShowAddForm] = useState(false);
  const [asOfDate, setAsOfDate] = useState(localStorage.getItem("asOfDate") || "As of June 30, 2025");
  const [isEditingDate, setIsEditingDate] = useState(false);
  const [statusYearLabel, setStatusYearLabel] = useState(localStorage.getItem("statusYearLabel") || "C.Y 2024 STATUS");
  const [targetYearLabel, setTargetYearLabel] = useState(localStorage.getItem("targetYearLabel") || "C.Y 2025 TARGET");
  const [accomplishmentYearLabel, setAccomplishmentYearLabel] = useState(localStorage.getItem("accomplishmentYearLabel") || "C.Y 2025 ACCOMPLISHMENT");

  const [isEditingStatusYear, setIsEditingStatusYear] = useState(false);
  const [isEditingTargetYear, setIsEditingTargetYear] = useState(false);
  const [isEditingAccomplishmentYear, setIsEditingAccomplishmentYear] = useState(false);

  const [successMessage, setSuccessMessage] = useState("");

  // Creates a stable key per entry to avoid collisions when program/indicator repeat
  const makeEntryKey = (program, entry, idx) => entry.id ?? entry.tempId ?? `${program.program}__${entry.indicator}__${idx}`;

  const [editableMonths, setEditableMonths] = useState(() => {
    const saved = localStorage.getItem("editableMonths");
    return saved ? JSON.parse(saved) : [
      "Jan-31", "Feb-28", "Mar-31", "Apr-30", "May-31", "Jun-30",
      "Jul-30", "Aug-30", "Sept-30", "Oct-30", "Nov-30", "Dec-30"
    ];
  });

  const handleAddProgram = () => {
    if (!allowEditing) return;
    setShowAddForm(true);
  };

  useEffect(() => {
    if (!allowEditing) {
      setShowAddForm(false);
      setIsEditingDate(false);
      setIsEditingStatusYear(false);
      setIsEditingTargetYear(false);
      setIsEditingAccomplishmentYear(false);
      setShowDropdown(false);
    }
  }, [allowEditing]);

  useEffect(() => {
    localStorage.setItem("asOfDate", asOfDate);
  }, [asOfDate]);

  // Helper to load months data from localStorage (returns an object keyed by program+indicator)
  const loadMonthsFromLocalStorage = () => {
    try {
      const raw = localStorage.getItem("convergenceMonths");
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  };

  const saveMonthsToLocalStorage = (monthsData) => {
    localStorage.setItem("convergenceMonths", JSON.stringify(monthsData));
  };

  useEffect(() => {
    if (!loading && !error) {
      const transformed = data.length ? transformFromApi(data) : initialState();

      // Load months data from localStorage
      const storedMonths = loadMonthsFromLocalStorage();

      // Merge stored months into transformed data
      const mergedData = transformed.map(prog => ({
        ...prog,
        data: prog.data.map((entry, entryIdx) => {
          // Compose unique key to identify each entry
          const key = makeEntryKey(prog, entry, entryIdx);
          if (storedMonths[key]) {
            // Override months from localStorage, keep the rest from API
            return {
              ...entry,
              months: storedMonths[key]
            };
          }
          return entry;
        })
      }));

      setProgramData(mergedData);
    }
  }, [data, loading, error]);

  const handleInputChange = (pIdx, iIdx, field, value, mIdx = null) => {
    if (!allowEditing) return;
    setProgramData(prev => {
      const next = [...prev];
      const program = next[pIdx];
      const entry = program?.data?.[iIdx];

      // Bail out if indices are out of sync (e.g., filtered view)
      if (!entry) return next;

      // Ensure months array is always present
      if (!Array.isArray(entry.months)) {
        entry.months = Array(12).fill("");
      }

      if (field === "months") {
        entry.months[mIdx] = value;

        // Save to localStorage for this specific program + indicator
        const key = makeEntryKey(program, entry, iIdx);
        const storedMonths = loadMonthsFromLocalStorage();
        storedMonths[key] = entry.months;
        saveMonthsToLocalStorage(storedMonths);
      } else {
        entry[field] = value;
      }

      return next;
    });
  };

  const handleSave = async () => {
    if (!allowEditing) return;
    const payload = flattenForApi(programData).map(row => ({
      id: row.id,
      type: row.type,
      program: row.program,
      indicator: row.indicator,
      status_2024: row.status_2024,
      target_2025: row.target_2025,
      total: row.total,
      status_date: row.status_date,
      remarks: row.remarks
    }));

    try {
      const saved = await upsertRows(payload);
      const merged = mergeResponse(programData, saved);
      setProgramData(merged);
      setSuccessMessage("🎉 Saved successfully!");
      setTimeout(() => setSuccessMessage(""), 3000); // Hide after 3 seconds
    } catch {
      alert("Save failed");
    }

  };

  const handleImport = async (e) => {
    if (!allowEditing) return;
    console.log("Import triggered");
    const file = e.target.files[0];
    if (!file) return;

    const workbook = new ExcelJS.Workbook();
    const reader = new FileReader();

    reader.onload = async (event) => {
      await workbook.xlsx.load(event.target.result);
      const worksheet = workbook.worksheets[0];

      const rows = [];
      worksheet.eachRow((row, rowIndex) => {
        console.log("Row:", rowIndex, row.values);
        if (rowIndex <= 3) return; // Skip title and header rows

        const rowData = {
          program: row.getCell(1).value,
          indicator: row.getCell(2).value,
          status_2024: row.getCell(3).value || "",
          target_2025: row.getCell(4).value || "",
          months: Array.from({ length: 12 }, (_, i) => row.getCell(5 + i).value || ""),
          total: row.getCell(17).value || "",
          status_date: row.getCell(18).value || "",
          remarks: row.getCell(19).value || ""
        };

        // Skip if no program or indicator
        if (!rowData.program || !rowData.indicator) return;

        rows.push(rowData);
      });

      // Transform into grouped format
      const grouped = {};
      rows.forEach(r => {
        if (!grouped[r.program]) {
          grouped[r.program] = {
            type: "Imported", // fallback type
            program: r.program,
            data: []
          };
        }
        grouped[r.program].data.push({
          id: null,
          indicator: r.indicator,
          status2024: r.status_2024,
          target2025: r.target_2025,
          months: r.months,
          total: r.total,
          statusToDate: r.status_date,
          remarks: r.remarks
        });
      });

      const importedData = Object.values(grouped);

      // Merge imported into programData by replacing matching program + indicator
      setPreviewData(importedData);
      setShowPreviewModal(true);


      setSuccessMessage("✅ Import successful!");
      setTimeout(() => setSuccessMessage(""), 3000);
    };

    reader.readAsArrayBuffer(file);
  };



  const handleExport = async () => {
    if (!allowEditing) return;
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Convergence Monitoring');

    // Title row
    worksheet.mergeCells('A1:Q1');
    const titleCell = worksheet.getCell('A1');
    titleCell.value = "CONVERGENCE PROGRAM MONITORING FORM";
    titleCell.font = { name: 'Calibri', size: 16, bold: true };
    titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
    titleCell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFD966' } // light yellow background
    };

    // Subtitle row
    worksheet.mergeCells('A2:Q2');
    const subtitleCell = worksheet.getCell('A2');
    subtitleCell.value = `As of ${asOfDate}`;
    subtitleCell.font = { name: 'Calibri', size: 12, italic: true };
    subtitleCell.alignment = { horizontal: 'center' };

    // Header row
    const headers = [
      "PROGRAM",
      "INDICATOR",
      "2024 STATUS",
      "2025 TARGET",
      ...editableMonths,
      "TOTAL",
      "STATUS TO DATE",
      "REMARKS"
    ];
    const headerRow = worksheet.addRow(headers);

    // Style the header row
    headerRow.eachCell(cell => {
      cell.font = { bold: true };
      cell.alignment = { horizontal: 'center' };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'BDD7EE' } // light blue
      };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };
    });

    // Add data rows
    programData.forEach(program => {
      program.data.forEach(entry => {
        const row = worksheet.addRow([
          program.program,
          entry.indicator,
          entry.status2024,
          entry.target2025,
          ...editableMonths.map(month => entry.months[editableMonths.indexOf(month)] || ''),
          entry.total,
          entry.statusToDate,
          entry.remarks
        ]);

        row.eachCell(cell => {
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          };
          cell.alignment = { vertical: 'middle' };
        });
      });
    });

    // Auto width
    worksheet.columns.forEach(column => {
      column.width = 18;
    });

    // Save file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    saveAs(blob, `ConvergenceMonitoring_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };


  const handleConfirmImport = () => {
    if (!allowEditing) return;
    setProgramData(prev => {
      const merged = [...prev];

      previewData.forEach(importProg => {
        const matchIndex = merged.findIndex(p => p.program === importProg.program);
        if (matchIndex !== -1) {
          merged[matchIndex] = {
            ...merged[matchIndex],
            data: importProg.data
          };
        } else {
          merged.push(importProg);
        }
      });

      return merged;
    });

    setPreviewData([]);
    setShowPreviewModal(false);
    setSuccessMessage("✅ Import saved!");
    setTimeout(() => setSuccessMessage(""), 3000);
  };



  useEffect(() => {
    localStorage.setItem("statusYearLabel", statusYearLabel);
  }, [statusYearLabel]);

  useEffect(() => {
    localStorage.setItem("targetYearLabel", targetYearLabel);
  }, [targetYearLabel]);

  useEffect(() => {
    localStorage.setItem("accomplishmentYearLabel", accomplishmentYearLabel);
  }, [accomplishmentYearLabel]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".dropdown")) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  if (loading) return <p>Loading…</p>;
  if (error) return <p>Error: {error.message}</p>;

  const handleBackClick = () => {
    const pendingCisReturn = localStorage.getItem('cisMachineriesReturn');
    if (pendingCisReturn && typeof onSelect === 'function') {
      onSelect('CIS');
      return;
    }

    if (typeof onBack === 'function') {
      onBack();
      return;
    }

    navigate(-1);
  };

  return (
    <PageWrapper>
      <BackButton type="button" onClick={handleBackClick}>Back</BackButton>
      <ContentCard>
        <HeaderContainer>
          <HeaderLeft>
            <HeaderText>
              <h2>CONVERGENCE PROGRAM MONITORING FORM</h2>
            </HeaderText>
          </HeaderLeft>

          {allowEditing && (
            <RightControls>
              <StyledButton onClick={handleAddProgram}>Add Program</StyledButton>

              <DropdownContainer>
                <StyledButton onClick={() => setShowDropdown(prev => !prev)}>
                  Manage Data ▾
                </StyledButton>
                {showDropdown && (
                  <DropdownMenu>
                    <DropdownItem onClick={() => { handleExport(); setShowDropdown(false); }}>
                      Export
                    </DropdownItem>
                    <DropdownItem onClick={() => { fileInputRef.current.click(); setShowDropdown(false); }}>
                      Import
                    </DropdownItem>
                    <input
                      type="file"
                      accept=".xlsx, .xls"
                      ref={fileInputRef}
                      style={{ display: 'none' }}
                      onChange={handleImport}
                    />
                  </DropdownMenu>
                )}
              </DropdownContainer>

              <SaveButton onClick={handleSave} disabled={saving}>
                {saving ? "Saving..." : "Save"}
              </SaveButton>
            </RightControls>
          )}
        </HeaderContainer>

        {successMessage && <Toast>{successMessage}</Toast>}

        {allowEditing && (
          <RightPanel darkMode={false} show={showAddForm}>
            <ConvergenceAdd
              darkMode={false}
              onClose={() => setShowAddForm(false)}
            />
          </RightPanel>
        )}

        <AsOfDateSection>
          {isEditingDate && allowEditing ? (
            <AsOfDateInput
              value={asOfDate}
              onChange={(e) => setAsOfDate(e.target.value)}
              onBlur={() => setIsEditingDate(false)}
              onKeyDown={(e) => e.key === "Enter" && setIsEditingDate(false)}
              autoFocus
            />
          ) : (
            <AsOfDateDisplay
              $editable={allowEditing}
              onClick={allowEditing ? () => setIsEditingDate(true) : undefined}
            >
              {asOfDate}
            </AsOfDateDisplay>
          )}
        </AsOfDateSection>

        <RegionTitle>Region 7</RegionTitle>

        <FiltersRow>
          <FilterField>
            <FilterLabel>Program</FilterLabel>
            <FilterSelect
              id="programFilter"
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
            >
              <option value="">All</option>
              {[...new Set(programData.map((p) => p.type))].map((type, idx) => (
                <option key={idx} value={type}>
                  {type}
                </option>
              ))}
            </FilterSelect>
          </FilterField>
        </FiltersRow>

        <TableSection>
          <TableScrollWrapper id="scrollContent">
            <TableScroller>
              <ResponsiveTable>
                <thead>
                  <tr style={{ backgroundColor: "#2e7d32", color: "white", position: "sticky", top: 0 }}>
                    <th rowSpan="2" style={tableCellStyle}>PROGRAM</th>
                    <th rowSpan="2" style={tableCellStyle}>INDICATOR</th>
                    <th rowSpan="2" style={tableCellStyle}>
                      {isEditingStatusYear && allowEditing ? (
                        <EditableHeaderInput
                          value={statusYearLabel}
                          onChange={(e) => setStatusYearLabel(e.target.value)}
                          onBlur={() => setIsEditingStatusYear(false)}
                          onKeyDown={(e) => e.key === "Enter" && setIsEditingStatusYear(false)}
                          autoFocus
                        />
                      ) : (
                        <HeaderEditableText
                          $editable={allowEditing}
                          onClick={allowEditing ? () => setIsEditingStatusYear(true) : undefined}
                        >
                          {statusYearLabel}
                        </HeaderEditableText>
                      )}
                    </th>

                    <th rowSpan="2" style={tableCellStyle}>
                      {isEditingTargetYear && allowEditing ? (
                        <EditableHeaderInput
                          value={targetYearLabel}
                          onChange={(e) => setTargetYearLabel(e.target.value)}
                          onBlur={() => setIsEditingTargetYear(false)}
                          onKeyDown={(e) => e.key === "Enter" && setIsEditingTargetYear(false)}
                          autoFocus
                        />
                      ) : (
                        <HeaderEditableText
                          $editable={allowEditing}
                          onClick={allowEditing ? () => setIsEditingTargetYear(true) : undefined}
                        >
                          {targetYearLabel}
                        </HeaderEditableText>
                      )}
                    </th>

                    <th colSpan={12} style={tableCellStyle}>
                      {isEditingAccomplishmentYear && allowEditing ? (
                        <EditableHeaderInput
                          value={accomplishmentYearLabel}
                          onChange={(e) => setAccomplishmentYearLabel(e.target.value)}
                          onBlur={() => setIsEditingAccomplishmentYear(false)}
                          onKeyDown={(e) => e.key === "Enter" && setIsEditingAccomplishmentYear(false)}
                          autoFocus
                          style={{ fontSize: 15 }}
                        />
                      ) : (
                        <HeaderEditableText
                          $editable={allowEditing}
                          onClick={allowEditing ? () => setIsEditingAccomplishmentYear(true) : undefined}
                        >
                          {accomplishmentYearLabel}
                        </HeaderEditableText>
                      )}
                    </th>

                    <th rowSpan="2" style={tableCellStyle}>TOTAL</th>
                    <th rowSpan="2" style={tableCellStyle}>STATUS TO DATE</th>
                    <th rowSpan="2" style={tableCellStyle}>REMARKS</th>
                  </tr>
                  <tr style={{ backgroundColor: "#3eaf27", color: "white" }}>
                    {editableMonths.map((month, i) => (
                      <th key={i} style={monthCellStyle}>
                        <HeaderMonthInput
                          type="text"
                          value={month}
                          readOnly={!allowEditing}
                          onChange={allowEditing ? (e) => {
                            const newMonths = [...editableMonths];
                            newMonths[i] = e.target.value;
                            setEditableMonths(newMonths);
                          } : undefined}
                          onBlur={allowEditing ? () => {
                            localStorage.setItem("editableMonths", JSON.stringify(editableMonths));
                          } : undefined}
                          onKeyDown={allowEditing ? (e) => {
                            if (e.key === "Enter") e.target.blur();
                          } : undefined}
                        />
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {Object.entries(groupedByType).map(([type, programs]) => (
                    <React.Fragment key={type}>
                      <tr style={{ backgroundColor: "#ffff00", fontWeight: "bold" }}>
                        <td colSpan="20" style={{ ...tableCellStyle, textAlign: "left", paddingLeft: "12px" }}>
                          ({type})
                        </td>
                      </tr>

                      {programs.map((program) => {
                        const programIndex = programData.findIndex(
                          (p) => p.type === program.type && p.program === program.program
                        );

                        if (programIndex === -1) return null;

                        return program.data.map((entry, iIdx) => {
                          const monthsArray = Array.isArray(entry.months) ? entry.months : Array(12).fill("");

                          return (
                            <tr key={`${program.program}-${iIdx}`}>
                              {iIdx === 0 && (
                                <td rowSpan={program.data.length} style={tableCellStyle}>{program.program}</td>
                              )}
                              <td style={tableCellStyle}>{entry.indicator}</td>

                              <td style={tableCellStyle}>
                                <TableInput
                                  type="text"
                                  value={entry.status2024}
                                  readOnly={!allowEditing}
                                  onChange={e => handleInputChange(programIndex, iIdx, "status2024", e.target.value)}
                                />
                              </td>

                              <td style={tableCellStyle}>
                                <TableInput
                                  type="text"
                                  value={entry.target2025}
                                  readOnly={!allowEditing}
                                  onChange={e => handleInputChange(programIndex, iIdx, "target2025", e.target.value)}
                                />
                              </td>

                              {monthsArray.map((monthValue, mIdx) => (
                                <td key={mIdx} style={monthCellStyle}>
                                  <TableInput
                                    type="text"
                                    value={monthValue}
                                    readOnly={!allowEditing}
                                    onChange={e => handleInputChange(programIndex, iIdx, "months", e.target.value, mIdx)}
                                  />
                                </td>
                              ))}

                              <td style={tableCellStyle}>
                                <TableInput
                                  type="text"
                                  value={entry.total}
                                  readOnly={!allowEditing}
                                  onChange={e => handleInputChange(programIndex, iIdx, "total", e.target.value)}
                                />
                              </td>

                              <td style={tableCellStyle}>
                                <TableInput
                                  type="text"
                                  value={entry.statusToDate}
                                  readOnly={!allowEditing}
                                  onChange={e => handleInputChange(programIndex, iIdx, "statusToDate", e.target.value)}
                                />
                              </td>

                              <td style={tableCellStyle}>
                                <TableInput
                                  type="text"
                                  value={entry.remarks}
                                  readOnly={!allowEditing}
                                  onChange={e => handleInputChange(programIndex, iIdx, "remarks", e.target.value)}
                                />
                              </td>
                            </tr>
                          );
                        });
                      })}
                    </React.Fragment>
                  ))}
                </tbody>
              </ResponsiveTable>
            </TableScroller>
          </TableScrollWrapper>
        </TableSection>

        {showPreviewModal && (
          <ConvergenceImportPreview
            previewData={previewData}
            programData={programData}
            onConfirm={handleConfirmImport}
            onClose={() => setShowPreviewModal(false)}
          />
        )}
      </ContentCard>
    </PageWrapper>
  );
}

export default ConvergenceMonitoringForm;

// Styles
const inputStyle = {
  width: "100%",
  border: "none",
  padding: "4px",
  boxSizing: "border-box",
  fontSize: "clamp(13px, 1.6vw, 15px)",
  background: "transparent"
};

const tableCellStyle = {
  border: "1px solid #ccc",
  padding: "6px 8px",
  textAlign: "center",
  fontSize: "13px",
};

const monthCellStyle = {
  ...tableCellStyle,
  width: "74px",
  minWidth: "72px",
  maxWidth: "78px",
  whiteSpace: "nowrap",
};

const PageWrapper = styled.div`
  width: 100%;
  padding: 14px 18px 20px;
  background: #e7f2ec;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  overflow: hidden;
`;

const BackButton = styled.button`
  position: fixed;
  left: 74px;
  bottom: 18px;
  z-index: 40;
  padding: 8px 14px;
  border: 1px solid #2e7d32;
  border-radius: 4px;
  background-color: #ffffff;
  color: #2e7d32;
  font-size: 14px;
  cursor: pointer;

  &:hover {
    background-color: #f2fff2;
  }
`;

const ContentCard = styled.div`
  width: min(1380px, 100%);
  background: #e7f2ec;
  border-radius: 0;
  padding: 10px 8px 0;
  box-shadow: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    border-radius: 18px;
    padding: clamp(16px, 5vw, 22px);
  }

  @media (max-width: 480px) {
    padding: clamp(14px, 6vw, 20px);
  }

  & > * {
    position: relative;
    z-index: 1;
  }
`;

const HeaderContainer = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-start;
  justify-content: space-between;

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 220px;
`;

const HeaderText = styled.div`
  margin-bottom: 0;

  h2 {
    margin: 0;
    font-size: clamp(24px, 3vw, 40px);
    font-weight: 800;
    color: #1c1c1c;
    text-align: left;
  }

  p {
    margin: 0;
    font-size: clamp(14px, 2vw, 16px);
    color: rgba(22, 54, 31, 0.7);
  }

  @media (max-width: 768px) {
    text-align: center;

    h2, p {
      text-align: center;
    }
  }
`;

const RightControls = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  justify-content: flex-end;
  margin-left: auto;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const StyledButton = styled.button`
  margin: 0;
  padding: 8px 16px;
  background-color: #ececec;
  border: 1px solid #a5a5a5;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.15s;
  font-size: 14px;
  font-weight: 600;
  color: #222;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: 108px;

  &:hover {
    background-color: #e2e2e2;
  }

  @media (max-width: 480px) {
    width: 100%;
  }
`;

const SaveButton = styled(StyledButton)`
  background-color: #2e7d32;
  color: white;
  border: 1px solid #2e7d32;

  &:hover {
    background-color: #276a2b;
  }

  &:disabled {
    background-color: #a5d6a7;
    cursor: not-allowed;
    transform: none;
  }
`;

const AsOfDateSection = styled.div`
  width: 100%;
  margin-top: 0;
`;

const AsOfDateDisplay = styled.p`
  margin: 0;
  font-size: 16px;
  font-weight: 500;
  color: #111;
  cursor: ${({ $editable }) => ($editable ? "pointer" : "default")};
  text-decoration: underline;
  display: inline-block;
  padding: 0;
`;

const AsOfDateInput = styled.input`
  font-size: 16px;
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid #777;
  outline: none;
  width: min(260px, 100%);
  background: #fff;
`;

const RegionTitle = styled.h3`
  font-size: 15px;
  font-weight: 700;
  margin: 0;
  color: #222;
  letter-spacing: 0;
`;

const FiltersRow = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 16px;
  justify-content: flex-start;

  @media (max-width: 560px) {
    align-items: stretch;
  }
`;

const FilterField = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
`;

const FilterLabel = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #222;
`;

const FilterSelect = styled.select`
  border: 1px solid #9b9b9b;
  border-radius: 2px;
  padding: 6px 10px;
  font-size: 14px;
  background: #fff;
  color: #111;
  outline: none;
  appearance: auto;
  min-width: min(420px, 75vw);
`;

const TableSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TableScrollWrapper = styled.div`
  width: 100%;
  border-radius: 0;
  background: #dcdcdc;
  border: 1px solid #b0b0b0;
  overflow: hidden;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const TableScroller = styled.div`
  overflow: auto;
  max-height: 520px;
  scrollbar-width: auto;
  scrollbar-color: #9b9b9b #dcdcdc;

  &::-webkit-scrollbar {
    height: 8px;
    width: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background: #9b9b9b;
    border-radius: 0;
  }

  &::-webkit-scrollbar-track {
    background: #dcdcdc;
  }
`;

const ResponsiveTable = styled.table`
  width: 100%;
  min-width: 1480px;
  border-collapse: collapse;
  background: #ffffff;
`;

const HeaderEditableText = styled.span`
  display: inline-block;
  cursor: ${({ $editable }) => ($editable ? "pointer" : "default")};
  text-decoration: underline;
`;

const EditableHeaderInput = styled.input`
  width: 100%;
  border: 1px solid #9f9f9f;
  border-radius: 2px;
  padding: 2px 6px;
  background: #fff;
  font-size: 13px;
  text-align: center;
  outline: none;
`;

const HeaderMonthInput = styled.input`
  width: 100%;
  border: none;
  background: transparent;
  color: white;
  font-weight: 600;
  text-align: center;
  font-size: 14px;
  cursor: ${({ readOnly }) => (readOnly ? "default" : "text")};
  outline: none;
  display: block;
  word-break: break-word;
`;

const TableInput = styled.input`
  ${inputStyle}
  color: #1f1f1f;
  padding: 0;
  border-radius: 0;
  background: transparent;
  border: none;
  text-align: center;

  &:focus {
    outline: none;
    background: rgba(46, 125, 50, 0.12);
  }

  &:read-only {
    background: transparent;
  }
`;

const RightPanel = styled.div`
  position: fixed;
  top: 0;
  right: ${({ show }) => (show ? '0' : '-45%')};
  width: 43%;
  height: 100%;
  background-color: ${({ darkMode }) => (darkMode ? 'transparent' : '#fff')};
  background-image: ${({ darkMode }) => (darkMode ? `url(${adminBg})` : 'none')};
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  box-shadow: -4px 0 10px rgba(0, 0, 0, 0.2);
  z-index: 1200;
  padding: clamp(50px, 3vw, 24px);
  box-sizing: border-box;
  transition: right 0.3s ease-in-out;

  @media (max-width: 1024px) {
    width: min(420px, 100%);
    right: ${({ show }) => (show ? '0' : '-100%')};
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const Toast = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  background: #4caf50;
  color: white;
  padding: 12px 20px;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0,0,0,0.15);
  font-weight: bold;
  z-index: 9999;
  animation: fadein 0.5s, fadeout 0.5s 2.5s;

  @keyframes fadein {
    from { opacity: 0; transform: translateY(-10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes fadeout {
    from { opacity: 1; transform: translateY(0); }
    to { opacity: 0; transform: translateY(-10px); }
  }
`;

const DropdownContainer = styled.div`
  position: relative;
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background-color: white;
  border: 1px solid #ccc;
  border-radius: 4px;
  width: 120px;
  z-index: 1000;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
`;

const DropdownItem = styled.div`
  padding: 8px 12px;
  cursor: pointer;
  font-size: 14px;
  color: #333;

  &:hover {
    background-color: #f0f0f0;
  }
`;

const modalStyle = {
  position: 'fixed',
  top: '10%',
  left: '10%',
  width: '80%',
  maxHeight: '80%',
  overflowY: 'auto',
  backgroundColor: 'white',
  padding: '20px',
  border: '2px solid #ccc',
  borderRadius: '8px',
  zIndex: 10000,
  boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
};
